import { useEffect, useState } from 'react';

const chatMessages = [
  { sender: 'customer', text: 'Hi, I need my HVAC system serviced', time: '2:34 PM' },
  { sender: 'ai', text: 'Hello! I\'d be happy to help schedule your HVAC service. What day works best for you?', time: '2:34 PM' },
  { sender: 'customer', text: 'How about Thursday afternoon?', time: '2:35 PM' },
  { sender: 'ai', text: 'Perfect! I have availability at 2:00 PM or 4:00 PM on Thursday. Which time works better?', time: '2:35 PM' },
  { sender: 'customer', text: '2:00 PM sounds great', time: '2:36 PM' },
  { sender: 'ai', text: 'Excellent! I\'ve scheduled your HVAC service for Thursday at 2:00 PM. You\'ll receive a confirmation shortly.', time: '2:36 PM' }
];

const timeSlots = [
  { time: '9:00 AM', status: 'available' },
  { time: '11:00 AM', status: 'available' },
  { time: '2:00 PM', status: 'booked' },
  { time: '4:00 PM', status: 'available' }
];

export default function DemoExperience() {
  const [visibleMessages, setVisibleMessages] = useState(0);
  const [activeSlot, setActiveSlot] = useState(-1);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleMessages((prev) => {
        if (prev < chatMessages.length) {
          return prev + 1;
        }
        return prev;
      });
    }, 1500);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (visibleMessages === chatMessages.length) {
      setTimeout(() => setActiveSlot(2), 500);
    }
  }, [visibleMessages]);

  return (
    <section className="relative py-32 bg-black overflow-hidden">
      {/* Central Massive Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[1200px] bg-gradient-to-r from-cyan-500/20 via-blue-600/20 to-purple-600/20 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20 scroll-reveal">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Experience AI in Action
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Watch how Bayfront Intelligence captures leads, engages customers, and books appointments automatically
          </p>
        </div>

        {/* Demo Interface */}
        <div className="max-w-7xl mx-auto scroll-reveal">
          <div className="bg-white/5 backdrop-blur-2xl border border-cyan-400/30 rounded-3xl p-8 md:p-12 shadow-[0_0_100px_rgba(0,212,255,0.3)]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left: Chat Interface */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  <h3 className="text-xl font-bold text-white">Live AI Conversation</h3>
                </div>

                <div className="bg-black/40 backdrop-blur-xl border border-cyan-400/20 rounded-2xl p-6 h-[500px] overflow-y-auto space-y-4">
                  {chatMessages.slice(0, visibleMessages).map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${message.sender === 'ai' ? 'justify-start' : 'justify-end'} animate-slideIn`}
                    >
                      <div className={`max-w-[80%] ${message.sender === 'ai' ? 'bg-gradient-to-br from-cyan-400/20 to-blue-600/20 border-cyan-400/30' : 'bg-white/10 border-white/20'} border backdrop-blur-xl rounded-2xl px-4 py-3`}>
                        <p className="text-white text-sm mb-1">{message.text}</p>
                        <span className="text-xs text-gray-400">{message.time}</span>
                      </div>
                    </div>
                  ))}
                  {visibleMessages < chatMessages.length && (
                    <div className="flex justify-start">
                      <div className="bg-gradient-to-br from-cyan-400/20 to-blue-600/20 border border-cyan-400/30 backdrop-blur-xl rounded-2xl px-4 py-3">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" />
                          <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                          <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Calendar Interface */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
                  <h3 className="text-xl font-bold text-white">Smart Scheduling</h3>
                </div>

                <div className="bg-black/40 backdrop-blur-xl border border-cyan-400/20 rounded-2xl p-6 h-[500px]">
                  <div className="mb-6">
                    <div className="text-2xl font-bold text-white mb-2">Thursday, May 15</div>
                    <div className="text-sm text-gray-400">Available Time Slots</div>
                  </div>

                  <div className="space-y-3">
                    {timeSlots.map((slot, index) => (
                      <div
                        key={index}
                        className={`p-4 rounded-xl border transition-all duration-500 ${
                          index === activeSlot
                            ? 'bg-gradient-to-r from-green-500/20 to-emerald-600/20 border-green-400/50 shadow-[0_0_30px_rgba(34,197,94,0.3)]'
                            : slot.status === 'booked'
                            ? 'bg-gradient-to-r from-cyan-500/20 to-blue-600/20 border-cyan-400/50'
                            : 'bg-white/5 border-white/10 hover:border-cyan-400/30'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <i className={`ri-time-line text-xl ${index === activeSlot ? 'text-green-400' : slot.status === 'booked' ? 'text-cyan-400' : 'text-gray-400'}`} />
                            <span className={`font-semibold ${index === activeSlot ? 'text-green-400' : slot.status === 'booked' ? 'text-cyan-400' : 'text-white'}`}>
                              {slot.time}
                            </span>
                          </div>
                          {index === activeSlot && (
                            <div className="flex items-center gap-2 animate-slideIn">
                              <i className="ri-checkbox-circle-fill text-green-400 text-xl" />
                              <span className="text-green-400 font-bold text-sm">Just Booked!</span>
                            </div>
                          )}
                          {slot.status === 'booked' && index !== activeSlot && (
                            <span className="text-cyan-400 text-sm font-semibold">Booked</span>
                          )}
                          {slot.status === 'available' && index !== activeSlot && (
                            <span className="text-gray-400 text-sm">Available</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Data Flow Visualization */}
                  <div className="mt-8 pt-8 border-t border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="text-center">
                        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2 bg-cyan-400/20 rounded-full border border-cyan-400/50">
                          <i className="ri-user-line text-cyan-400 text-xl" />
                        </div>
                        <div className="text-xs text-gray-400">Lead</div>
                      </div>
                      <div className="flex-1 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-600 mx-2 relative">
                        <div className="absolute top-1/2 left-0 w-2 h-2 bg-cyan-400 rounded-full animate-ping" />
                      </div>
                      <div className="text-center">
                        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2 bg-blue-600/20 rounded-full border border-blue-400/50">
                          <i className="ri-robot-2-line text-blue-400 text-xl" />
                        </div>
                        <div className="text-xs text-gray-400">AI</div>
                      </div>
                      <div className="flex-1 h-0.5 bg-gradient-to-r from-blue-600 to-green-500 mx-2 relative">
                        <div className="absolute top-1/2 left-0 w-2 h-2 bg-blue-400 rounded-full animate-ping" style={{ animationDelay: '0.5s' }} />
                      </div>
                      <div className="text-center">
                        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2 bg-green-500/20 rounded-full border border-green-400/50">
                          <i className="ri-calendar-check-line text-green-400 text-xl" />
                        </div>
                        <div className="text-xs text-gray-400">Booked</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Status Labels */}
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <div className="px-4 py-2 bg-cyan-400/10 border border-cyan-400/30 rounded-full backdrop-blur-xl">
                <span className="text-cyan-400 text-sm font-semibold flex items-center gap-2">
                  <i className="ri-flashlight-fill" />
                  Instant Reply
                </span>
              </div>
              <div className="px-4 py-2 bg-blue-600/10 border border-blue-400/30 rounded-full backdrop-blur-xl">
                <span className="text-blue-400 text-sm font-semibold flex items-center gap-2">
                  <i className="ri-calendar-check-fill" />
                  Auto-Scheduled
                </span>
              </div>
              <div className="px-4 py-2 bg-green-500/10 border border-green-400/30 rounded-full backdrop-blur-xl">
                <span className="text-green-400 text-sm font-semibold flex items-center gap-2">
                  <i className="ri-database-2-fill" />
                  CRM Updated
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}